import { fetchWithAuth } from "~/common/fetch";
import { API_APPROVE_PLAN, API_REWORK_PLAN } from "~/config";

import type { DoctorViewerApi, PlanComment, ViewerPlan } from "./types";

const plansUrl = (patientId: number) => `${process.env.API}/doctor/patients/${patientId}/viewer-plans`;
const planUrl = (patientId: number, planId: number) => `${plansUrl(patientId)}/${planId}`;

async function request(url: string, init?: RequestInit): Promise<unknown> {
  const response = await fetchWithAuth(url, init);
  if (!response.ok) throw new Error(`Viewer request failed (${response.status})`);
  return response.status === 204 ? undefined : response.json();
}

// The doctor API contract is documented in docs/features/doctor-viewer.md.
export const doctorViewerApi: DoctorViewerApi = {
  async share(patientId, planId) {
    const response = (await request(`${planUrl(patientId, planId)}/link`)) as { path: string };
    const url = new URL(response.path, window.location.origin);
    if (url.origin !== window.location.origin || !url.pathname.startsWith("/viewer/plans/")) throw new Error("Invalid patient plan link");
    return url.href;
  },
  async list(patientId, signal) {
    const response = (await request(plansUrl(patientId), { signal })) as { plans: ViewerPlan[] };
    return response.plans.map((plan) => {
      const manifest = new URL(plan.manifest_url, window.location.href);
      if (manifest.origin !== window.location.origin) throw new Error("Viewer manifests must use the portal origin");
      return { ...plan, manifest_url: manifest.href };
    });
  },
  async comments(patientId, planId, signal) {
    const response = (await request(`${planUrl(patientId, planId)}/comments`, { signal })) as { comments: PlanComment[] };
    return response.comments;
  },
  async approve(patientId, planId) {
    await request(API_APPROVE_PLAN(patientId), { method: "POST", body: JSON.stringify({ viewer_plan_id: planId }) });
  },
  async rework(patientId, planId, comment) {
    await request(API_REWORK_PLAN(patientId), { method: "POST", body: JSON.stringify({ viewer_plan_id: planId, comment }) });
  },
  async comment(patientId, planId, comment) {
    await request(`${planUrl(patientId, planId)}/comments`, { method: "POST", body: JSON.stringify({ comment }) });
  },
};
